import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { config, img, reviews, getProduct, KIT_OFFERS } from "../config";
import { Badge, Stars, Reveal, SplitReveal, Marquee, Img, ReviewCard } from "../components/Primitives";
import { ProductCard } from "../components/ProductCard";
import { OfferSelector } from "../components/OfferSelector";
import { useCart } from "../context/CartContext";
import { useQuiz } from "../context/QuizContext";

const H2 = ({ children, ...p }) => (
  <h2 className="title-xl" style={{ fontSize: "clamp(30px, 6vw, 56px)", margin: 0 }} {...p}>{children}</h2>
);

export default function Home() {
  const { addItem } = useCart();
  const { openQuiz } = useQuiz();
  const kit = getProduct("kit");
  const [offerId, setOfferId] = useState("kit-decouverte");
  const offer = KIT_OFFERS.find((o) => o.id === offerId) || KIT_OFFERS[0];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <main data-testid="home-page">
      {/* S1 HERO */}
      <section ref={heroRef} className="grain" style={{ position: "relative", minHeight: "92vh", display: "flex", alignItems: "flex-end", overflow: "hidden", borderBottom: "1px solid var(--border)" }}>
        <motion.div style={{ position: "absolute", inset: 0, y, scale, zIndex: 0 }}>
          <video autoPlay muted loop playsInline poster={img.hero_poster} style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid="hero-video">
            <source src={img.hero_video} type="video/mp4" />
          </video>
        </motion.div>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.95) 100%)", zIndex: 1 }} />
        <div className="wrap" style={{ position: "relative", zIndex: 2, paddingBottom: 72, paddingTop: 120 }}>
          <div style={{ maxWidth: 640 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Badge testid="hero-badge">La routine dentaire nouvelle génération</Badge>
            </motion.div>
            <h1 className="title-xl" style={{ fontSize: "clamp(32px, 7vw, 66px)", fontWeight: 300, margin: "20px 0 24px" }}>
              <SplitReveal lines={["VOTRE ÉMAIL NE REPOUSSE", "PAS. NOURRISSEZ-LE."]} delay={0.15} />
            </h1>
            <Reveal delay={0.5}>
              <p style={{ color: "var(--text)", opacity: 0.9, fontSize: 17, maxWidth: "60ch", marginBottom: 28 }}>
                Vous avez tout changé. Sauf votre dentifrice. Des pastilles à la nano-hydroxyapatite qui nourrissent votre émail, pour un sourire reminéralisé.
              </p>
              <Link to="/kit" className="btn btn-primary" data-testid="hero-cta">Craquez pour CRUSH</Link>
              <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 20 }}>★ 4,6/5 · 225 avis vérifiés · Livraison offerte sur le kit · Satisfait ou remboursé 30 jours</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* S2 MARQUEE */}
      <Marquee text="NANO-HYDROXYAPATITE · NOURRIT L'ÉMAIL · SANS FLUOR · SANS TUBE · 1 PASTILLE = 1 BROSSAGE · RECHARGEABLE À VIE · DOSAGE PARFAIT · FORMAT VOYAGE" />

      {/* S3 PEUR ÉMAIL */}
      <section className="section" style={{ background: "var(--surface)" }} data-testid="section-enamel">
        <div className="wrap">
          <div className="crush-g2" style={{ gap: 48, alignItems: "center" }}>
            <Reveal>
              <H2 style={{ marginTop: 16 }}>VOTRE ÉMAIL NE REPOUSSE PAS.</H2>
              <p style={{ color: "var(--text-muted)", maxWidth: "60ch", marginTop: 20 }}>
                C'est le seul tissu de votre corps qui ne se régénère pas. Une fois usé, il ne revient jamais. Le fluor aide à le protéger. La nano-hydroxyapatite va plus loin : elle apporte à l'émail le minéral exact dont il est composé — le même qui constitue 97 % de sa structure — et contribue à le reminéraliser, brossage après brossage. Vous ne défendez plus vos dents. Vous les nourrissez.
              </p>
              <Link to="/science" className="btn btn-secondary" style={{ marginTop: 24 }} data-testid="enamel-cta">Comprendre la science</Link>
            </Reveal>
            <Reveal delay={0.15}>
              <Img src={img.enamel} alt="Émail dentaire" sizes="(min-width:900px) 50vw, 100vw" style={{ width: "100%", borderRadius: 4, border: "1px solid var(--border)" }} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* S3.5 QUIZ TEASER */}
      <section className="section" style={{ paddingTop: 56, paddingBottom: 56 }} data-testid="section-quiz-teaser">
        <div className="wrap" style={{ textAlign: "center", maxWidth: 720 }}>
          <Reveal>
            <Badge>Pas encore convaincu ?</Badge>
            <h2 className="title-xl" style={{ fontSize: "clamp(28px,6vw,48px)", margin: "18px 0 16px" }}>TROUVEZ VOTRE CRUSH EN 30 SECONDES.</h2>
            <p style={{ color: "var(--text-muted)", marginBottom: 28, maxWidth: "55ch", marginLeft: "auto", marginRight: "auto" }}>Trois questions, zéro email. On lève vos doutes et on vous oriente vers le produit fait pour vous.</p>
            <button className="btn btn-primary" data-testid="quiz-teaser-cta" onClick={openQuiz}>Faire le test</button>
          </Reveal>
        </div>
      </section>

      {/* S4 TUBE VS CRUSH */}
      <section className="section" data-testid="section-tube-vs">
        <div className="wrap">
          <Reveal><H2>DENTIFRICE CLASSIQUE VS CRUSH.</H2>
            <p style={{ color: "var(--text-muted)", marginTop: 12 }}>Le tube de dentifrice a été inventé en 1896. Il est temps d'en changer.</p>
          </Reveal>
          <div className="crush-g2" style={{ gap: 20, marginTop: 40 }}>
            {[
              { t: "LE TUBE — DEPUIS 1896", rows: [["900 M", "de tubes jetés chaque année en Europe"], ["30 %", "de produit gaspillé dans le tube"], ["0 %", "de recyclage effectif"], [null, "Actifs : fluor, SLS, conservateurs"]], muted: true },
              { t: "CRUSH — 2026", rows: [["0", "déchet plastique"], ["100 %", "du produit utilisé"], ["∞", "réutilisable à l'infini"], [null, "Actifs : hydroxyapatite, xylitol, huiles essentielles"]], muted: false },
            ].map((col, ci) => (
              <Reveal key={ci} delay={ci * 0.1}>
                <div className="card" style={{ padding: 28, height: "100%" }}>
                  <div className="eyebrow" style={{ color: col.muted ? "var(--text-muted)" : "var(--accent-light)", marginBottom: 20 }}>{col.t}</div>
                  {col.rows.map(([big, small], i) => (
                    <div key={i} style={{ padding: "14px 0", borderTop: i ? "1px solid var(--border)" : "none", display: "flex", alignItems: "baseline", gap: 16 }}>
                      {big && <span className="display" style={{ fontSize: 32, fontWeight: 700, color: col.muted ? "var(--text-muted)" : "var(--accent)", minWidth: 90 }}>{big}</span>}
                      <span style={{ color: "var(--text-muted)" }}>{small}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal><p style={{ color: "var(--text-muted)", marginTop: 28, fontStyle: "italic" }}>La routine dentaire vient de changer de génération.</p></Reveal>
        </div>
      </section>

      {/* S5 LE KIT */}
      <section className="section" style={{ background: "var(--surface)" }} data-testid="section-kit">
        <div className="wrap">
          <div className="card crush-g2" style={{ overflow: "hidden" }}>
            <Img src={img.kit_main} alt="Le Kit CRUSH" sizes="(min-width:900px) 50vw, 100vw" style={{ width: "100%", aspectRatio: "4/5", objectFit: "cover" }} />
            <div style={{ padding: 36, display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
              <Badge>L'essentiel.</Badge>
              <H2 style={{ fontSize: "clamp(28px,5vw,44px)" }}>LE KIT DENTAIRE COMPLET.</H2>
              <p style={{ color: "var(--text-muted)", maxWidth: "55ch" }}>Dentifrice, bain de bouche, brosse. Tout ce qu'il faut pour changer de routine en une seule décision. Le dentifrice nouvelle génération : un actif qui nourrit vraiment l'émail.</p>
              <p style={{ color: "var(--accent-light)", fontSize: 14, margin: 0, fontWeight: 600 }}>Boîte de transport en métal offerte</p>
              <Stars rating={kit.rating} count={kit.review_count} />
              <div style={{ marginTop: 4 }}>
                <OfferSelector selectedId={offerId} onSelect={setOfferId} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                <button className="btn btn-primary" data-testid="kit-cta-add" onClick={() => addItem(offerId, "Menthe")}>Ajouter au panier — {offer.price} €</button>
                <Link to="/kit" className="btn btn-secondary" data-testid="kit-cta-primary">Voir la fiche complète</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* S6 COMMENT ÇA MARCHE */}
      <section className="section" data-testid="section-how">
        <div className="wrap">
          <Reveal><H2>UNE PASTILLE. LA MÊME ROUTINE. EN MIEUX.</H2></Reveal>
          <div className="crush-g3" style={{ gap: 20, marginTop: 40 }}>
            {[
              ["CROQUEZ.", "Une pastille suffit. Dosage parfait, zéro gâchis."],
              ["BROSSEZ.", "Deux minutes, exactement comme d'habitude. Le geste ne change pas — il s'améliore."],
              ["RECOMMENCEZ.", "Un mois de routine dans une boîte qui tient dans la paume. Partout où vous allez."],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="card" style={{ padding: 28, height: "100%" }}>
                  <span className="display" style={{ fontSize: 44, color: "var(--accent)", fontWeight: 700 }}>{`0${i + 1}`}</span>
                  <h3 className="display" style={{ margin: "12px 0 8px", letterSpacing: "0.03em" }}>{t}</h3>
                  <p style={{ color: "var(--text-muted)", margin: 0 }}>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}><Img src={img.usage} alt="Utilisation" sizes="100vw" style={{ width: "100%", marginTop: 32, borderRadius: 4, border: "1px solid var(--border)", background: "var(--surface)", maxHeight: 520, objectFit: "contain" }} /></Reveal>
        </div>
      </section>

      {/* S7 SCIENCE condensé */}
      <section className="section" style={{ background: "var(--surface)" }} data-testid="section-science-teaser">
        <div className="wrap crush-g2" style={{ gap: 48 }}>
          <Reveal>
            <Badge>La technologie</Badge>
            <H2 style={{ marginTop: 16 }}>NÉE POUR L'ESPACE. PERFECTIONNÉE AU JAPON.</H2>
            <p style={{ color: "var(--text-muted)", maxWidth: "58ch", marginTop: 18 }}>La nano-hydroxyapatite est issue des recherches menées dès les années 1960 pour protéger le tissu osseux des astronautes. Le brevet est exploité au Japon depuis 1970, où la nHA est devenue le standard anticarie national. C'est le même minéral que celui qui compose 97 % de votre émail — aujourd'hui l'alternative au fluor la plus étudiée au monde.</p>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginTop: 28 }}>
              {[["1970", "brevet d'origine"], ["45+", "ans de recherche clinique"], ["97 %", "de votre émail est de l'hydroxyapatite"]].map(([n, l]) => (
                <div key={n}><div className="display" style={{ fontSize: 34, fontWeight: 700, color: "var(--accent)" }}>{n}</div><div style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 130 }}>{l}</div></div>
              ))}
            </div>
            <Link to="/science" className="btn btn-secondary" style={{ marginTop: 28 }} data-testid="science-teaser-cta">Voir les études</Link>
          </Reveal>
          <Reveal delay={0.15}><Img src={img.nha_tech} alt="Nano-hydroxyapatite" sizes="(min-width:900px) 50vw, 100vw" style={{ width: "100%", borderRadius: 4, border: "1px solid var(--border)" }} /></Reveal>
        </div>
        <div className="wrap" style={{ marginTop: 56 }}>
          {[
            "1960s — NASA : l'hydroxyapatite de synthèse est mise au point pour protéger le tissu osseux des astronautes.",
            "1970 — Le brevet est transféré à Sangi (Japon).",
            "1980 — Premier dentifrice à la nano-hydroxyapatite au monde.",
            "1993 — Le Japon reconnaît officiellement la nHA comme agent anticarie.",
            "2026 — CRUSH : la même technologie, en pastille. Sans tube, sans fluor.",
          ].map((line, i) => {
            const [year, ...rest] = line.split(" — ");
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 20, padding: "18px 0", borderTop: "1px solid var(--border)" }} data-testid={`tech-timeline-${i}`}>
                  <span className="display" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 20 }}>{year}</span>
                  <p style={{ margin: 0, color: "var(--text-muted)" }}>{rest.join(" — ")}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* S8 3 PREMIERS JOURS */}
      <section className="section" data-testid="section-days">
        <div className="wrap">
          <Reveal><H2>LES 3 PREMIERS JOURS.</H2><p style={{ color: "var(--text-muted)", marginTop: 12 }}>Toute nouvelle routine demande trois jours. Voilà les vôtres.</p></Reveal>
          <div style={{ marginTop: 36 }}>
            {[
              ["JOUR 1 — LA DÉCOUVERTE.", "Vous croquez, ça mousse, vous brossez. Le soir, la langue glisse sur des dents lisses comme après un détartrage."],
              ["JOUR 2 — L'ADAPTATION.", "Le geste devient naturel. Le tube ne vous manque pas — et votre étagère respire."],
              ["JOUR 3 — LA MAÎTRISE.", "Croquer est devenu un réflexe."],
              ["JOUR 4+ — LA NOUVELLE NORME.", "Le retour en arrière n'est plus envisageable."],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, padding: "22px 0", borderTop: "1px solid var(--border)" }}>
                  <span className="display" style={{ fontSize: 22, color: "var(--accent)", fontWeight: 700 }}>{`0${i + 1}`}</span>
                  <div><h3 className="display" style={{ margin: "0 0 6px", letterSpacing: "0.02em", fontSize: 18 }}>{t}</h3><p style={{ margin: 0, color: "var(--text-muted)" }}>{d}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* S9 GAMME */}
      <section className="section" style={{ background: "var(--surface)" }} data-testid="section-range">
        <div className="wrap">
          <Reveal><H2>COMPLÉTEZ VOTRE ROUTINE.</H2></Reveal>
          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", marginTop: 36 }}>
            {config.products.map((p, i) => (<Reveal key={p.id} delay={i * 0.05}><ProductCard product={p} imageOverride={["/media/pastilles-02.webp", "/media/pastilles-01.webp", "/media/bdb-02.webp", "/media/brosse-01.webp"][i]} /></Reveal>))}
          </div>
          <div style={{ marginTop: 32 }}><Link to="/boutique" className="btn btn-secondary" data-testid="range-cta">Toute la boutique</Link></div>
        </div>
      </section>

      {/* S10 AVIS */}
      <section className="section" data-testid="section-reviews">
        <div className="wrap">
          <Reveal><H2>ILS ONT CHANGÉ DE GÉNÉRATION.</H2><p style={{ color: "var(--text-muted)", marginTop: 12 }}>★ 4,6/5 — 225 avis vérifiés</p></Reveal>
          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", marginTop: 36 }}>
            {reviews.home.map((r, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <ReviewCard review={r} testid={`home-review-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* S11 FONDATRICE */}
      <section className="section" style={{ background: "var(--surface)" }} data-testid="section-founder">
        <div className="wrap crush-g2" style={{ gap: 48 }}>
          <Reveal><Img src={img.founder} alt="Fanny, fondatrice" sizes="(min-width:900px) 50vw, 100vw" style={{ width: "100%", aspectRatio: "4/5", objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} /></Reveal>
          <Reveal delay={0.15}>
            <H2>DERRIÈRE CRUSH. IL Y A MOI. FANNY.</H2>
            <p style={{ color: "var(--text-muted)", maxWidth: "58ch", marginTop: 20 }}>J'ai passé cinq ans à produire des semences. Mon métier, c'était le vivant : comprendre, sélectionner ce qui fonctionne, éliminer le superflu. Quand j'ai voulu appliquer la même exigence à ma salle de bain, tout y est passé — sauf ma routine dentaire. Aucune alternative n'était à la hauteur : soit efficace mais chimique, soit naturelle mais inefficace. Je ne l'ai pas trouvée. Alors je l'ai créée. Une pastille, un actif qui nourrit vraiment l'émail, et rien de superflu.</p>
            <p style={{ marginTop: 20, fontFamily: "var(--display)", fontWeight: 500 }}>Fanny, fondatrice</p>
            <a href={config.brand.instagram || "#"} className="btn btn-secondary" data-testid="founder-cta">Nous suivre sur Instagram</a>
          </Reveal>
        </div>
      </section>

      {/* S12 CTA FINAL */}
      <section className="section grain" style={{ background: "var(--surface)", textAlign: "center", position: "relative" }} data-testid="section-final-cta">
        <div className="wrap" style={{ position: "relative", zIndex: 2 }}>
          <Reveal><H2 style={{ fontSize: "clamp(36px,8vw,72px)" }}>CRAQUEZ POUR CRUSH.</H2>
            <div style={{ marginTop: 28 }}><Link to="/kit" className="btn btn-primary" data-testid="final-cta">Découvrir le kit — 45 €</Link></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
